"use client";

import { c as _c } from "react/compiler-runtime";
import { UploadIcon } from "lucide-react";
import { useRef, useState } from "react";
import { CreateSkillDialog } from "@/components/playground/create-skill-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { Skill } from "@/hooks/useSkills";
interface ParsedSkill {
  name: string;
  description: string;
  instructions: string;
}
function parseSkillMarkdown(content: string): ParsedSkill | null {
  const frontmatterMatch = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!frontmatterMatch) {
    return null;
  }
  const frontmatter = frontmatterMatch[1];
  const body = frontmatterMatch[2].trim();
  const nameMatch = frontmatter.match(/^name:\s*(.+)$/m);
  const descriptionMatch = frontmatter.match(/^description:\s*(.+)$/m);
  if (!nameMatch) {
    return null;
  }
  return {
    name: nameMatch[1].trim(),
    description: descriptionMatch?.[1].trim() ?? "",
    instructions: body
  };
}
interface UploadSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (skill: Skill) => void;
}
export function UploadSkillDialog(t0) {
  const $ = _c(39);
  const {
    open,
    onOpenChange,
    onSuccess
  } = t0;
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== onOpenChange) {
    t1 = file => {
      setError(null);
      if (file.size > 52428800) {
        setError("File size must not exceed 50 MB.");
        return;
      }
      if (!file.name.endsWith(".md")) {
        setError("Only .md files are supported for upload. .zip/.skill support coming soon.");
        return;
      }
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target?.result as string;
        const result = parseSkillMarkdown(content);
        if (!result) {
          setError("Could not parse skill. The .md file must contain YAML frontmatter with a `name` field.");
          return;
        }
        setParsed(result);
        onOpenChange(false);
        setCreateOpen(true);
      };
      reader.readAsText(file);
    };
    $[0] = onOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleFile = t1;
  let t2;
  if ($[2] !== handleFile) {
    t2 = e_0 => {
      e_0.preventDefault();
      setIsDragging(false);
      const file_0 = e_0.dataTransfer.files[0];
      if (file_0) {
        handleFile(file_0);
      }
    };
    $[2] = handleFile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleDrop = t2;
  let t3;
  if ($[4] !== handleFile) {
    t3 = e_1 => {
      const file_1 = e_1.target.files?.[0];
      if (file_1) {
        handleFile(file_1);
      }
    };
    $[4] = handleFile;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleInputChange = t3;
  let t4;
  if ($[6] !== onSuccess) {
    t4 = skill => {
      setParsed(null);
      onSuccess?.(skill);
    };
    $[6] = onSuccess;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCreateSuccess = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogHeader><DialogTitle>Upload skill</DialogTitle></DialogHeader>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `mt-2 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30"}`;
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_2 => {
      e_2.preventDefault();
      setIsDragging(true);
    };
    t8 = () => setIsDragging(false);
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => inputRef.current?.click();
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_3 => {
      if (e_3.key === "Enter" || e_3.key === " ") {
        inputRef.current?.click();
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <UploadIcon className="text-muted-foreground mb-2 h-8 w-8" />;
    t12 = <p className="text-muted-foreground text-sm">Drag and drop or click to upload</p>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  let t13;
  if ($[15] !== handleInputChange) {
    t13 = <input ref={inputRef} type="file" accept=".md" className="hidden" onChange={handleInputChange} />;
    $[15] = handleInputChange;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== handleDrop || $[18] !== t13 || $[19] !== t6) {
    t14 = <div className={t6} onDragOver={t7} onDragLeave={t8} onDrop={handleDrop} onClick={t9} role="button" tabIndex={0} onKeyDown={t10} aria-label="Upload skill file">{t11}{t12}{t13}</div>;
    $[17] = handleDrop;
    $[18] = t13;
    $[19] = t6;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== error) {
    t15 = error && <p className="text-destructive mt-2 text-sm">{error}</p>;
    $[21] = error;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">File requirements</p>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mt-4">{t16}<ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs"><li>.md file must contain a <code>name</code> field in YAML frontmatter</li><li>.zip or .skill file must include a SKILL.md file</li><li>File size must not exceed 50 MB</li></ul></div>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t14 || $[26] !== t15) {
    t18 = <DialogContent className="sm:max-w-[420px]">{t5}{t14}{t15}{t17}</DialogContent>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== onOpenChange || $[29] !== open || $[30] !== t18) {
    t19 = <Dialog open={open} onOpenChange={onOpenChange}>{t18}</Dialog>;
    $[28] = onOpenChange;
    $[29] = open;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== createOpen || $[33] !== handleCreateSuccess || $[34] !== parsed) {
    t20 = parsed && <CreateSkillDialog open={createOpen} onOpenChange={o => {
      setCreateOpen(o);
      if (!o) {
        setParsed(null);
      }
    }} initialValues={parsed} onSuccess={handleCreateSuccess} />;
    $[32] = createOpen;
    $[33] = handleCreateSuccess;
    $[34] = parsed;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t19 || $[37] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  return t21;
}